import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import {
  enqueueTask,
  restartableTask,
  dropTask,
  task,
} from 'ember-concurrency';
import { getObservable, queryManager } from 'ember-apollo-client';
import { v4 as uuid } from 'uuid';
import { variation } from 'ember-launch-darkly';
import sendMessageMutation from 'my-phorest/gql/mutations/send-chat-message.graphql';
import updateChatMessagesSeenStatusMutation from 'my-phorest/gql/mutations/update-chat-messages-seen-status.graphql';
import updateBranchChatAiSettingsMutation from 'my-phorest/gql/mutations/update-branch-chat-ai-settings.graphql';

import getChatMessagesQuery from 'my-phorest/gql/queries/chat-messages.graphql';
import getChatConversationsQuery from 'my-phorest/gql/queries/chat-conversations.graphql';
import getUnreadMessagesCountQuery from 'my-phorest/gql/queries/chat-unread-messages-count.graphql';
import getWhatsappTemplatesQuery from 'my-phorest/gql/queries/whatsapp-templates.graphql';
import getBranchChatAiSettingsQuery from 'my-phorest/gql/queries/branch-chat-ai-settings.graphql';

export default class TwoWayChatService extends Service {
  @queryManager apollo;
  @service session;
  @service apolloSubscriptions;
  @service eventPropagator;
  @service access;
  @service pendo;
  @service whatsapp;
  @service intl;
  @service notifications;

  @tracked screen = '';
  @tracked selectedClient = null;
  @tracked selectedChat = null;
  @tracked selectedConversation = null;
  @tracked selectedTab = 'all-chats';
  @tracked showUnreadConversationsOnly = false;
  @tracked selectedTemplate = null;
  @tracked branchAiSettings = null;
  @tracked lastMessage = null;

  getChatMessagesQuery = getChatMessagesQuery;

  constructor() {
    super(...arguments);
    this.eventPropagator.subscribe(
      'clientChatChange',
      this.refetchResourcesTask.perform
    );
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.eventPropagator.unsubscribe(
      'clientChatChange',
      this.refetchResourcesTask.perform
    );
  }

  get chatConversations() {
    return this.getChatConversationsTask.lastSuccessful?.value.chatConversations?.edges.map(
      (edge) => edge.node
    );
  }

  get chatConversationsChannels() {
    const channels = [];

    if (this.isTwoWayEnabled) {
      const smsPhoneNumber =
        this.session.branch.twoWayMessagingPhoneNumber ??
        this.session.branch.marketing.branchSmsNumber;
      channels.push({
        label: 'SMS',
        phoneNumber: smsPhoneNumber,
        name: 'SMS',
      });
    }

    if (
      variation('release-marketing-whatsapp-integration-p-10') &&
      this.whatsapp.hasSuccessfulConnection
    ) {
      const whatsappPhoneNumber =
        this.whatsapp.successfulConnection.displayPhoneNumber;
      channels.push({
        label: 'WhatsApp',
        phoneNumber: whatsappPhoneNumber,
        name: 'WHATSAPP',
      });
    }
    return channels;
  }

  get unreadMessagesCount() {
    const count =
      this.getUnreadMessagesCountTask.lastSuccessful?.value
        .chatUnreadMessageCount.count;
    return count > 99 ? '99+' : count;
  }

  get isWindowChatOpen() {
    return !!this.screen;
  }

  get hasSelectedClientPhoneNumber() {
    return !!this.selectedClient?.mobile;
  }

  get queryVariables() {
    return {
      clientId: this.selectedClient.id,
      channel: this.selectedConversation.channel,
    };
  }

  get whatsappTemplates() {
    return this.getWhatsappTemplatesTask.lastSuccessful?.value.edges.map(
      (edge) => edge.node
    );
  }

  get isTwoWayEnabled() {
    return (
      this.session.branch.isTwoWayMessagingEnabled &&
      this.access.hasPermission('two-way-chat')
    );
  }

  @action
  toggleWindowChat() {
    if (this.isWindowChatOpen) {
      this.closeWindowChatTask.perform();
    } else {
      this.openWindowChat();
    }
  }

  @action
  openWindowChat() {
    this.setScreenTask.perform('chats-list');
    // Track the usage of two-way chat feature
    this.pendo.trackEvent('Two-Way Chat - Feature Usage');
  }

  @action
  reset() {
    this.selectedClient = null;
    this.selectedChat = null;
    this.selectedConversation = null;
    this.selectedTab = 'all-chats';
    this.showUnreadConversationsOnly = false;
    this.selectedTemplate = null;
  }

  @action
  toggleShowUnreadConversationsOnly() {
    this.showUnreadConversationsOnly = !this.showUnreadConversationsOnly;
    this.getChatConversationsTask.perform();
  }

  @action
  selectClient(client, conversation = null) {
    this.selectedClient = client;
    this.selectedConversation = conversation;
  }

  @action
  setNextScreen(nextScreen) {
    this.setScreenTask.perform(nextScreen);
  }

  subscribe() {
    const hasTwoWayPermission = this.access.hasPermission('two-way-chat');

    if (this.isTwoWayEnabled) {
      this.getUnreadMessagesCountTask.perform();
      this.apolloSubscriptions.subscribe('chatChange', {
        accountId: this.session.accountId,
      });
    }

    // A check will be included in branch settings for the milestone 2
    if (hasTwoWayPermission) {
      this.whatsapp.getWhatsAppConnectionsTask.perform();
    }
  }

  @restartableTask
  *getChatConversationsTask() {
    return yield this.apollo.query({
      query: getChatConversationsQuery,
      variables: {
        unread: this.showUnreadConversationsOnly ? true : undefined,
        staffMemberId:
          this.selectedTab === 'my-chats'
            ? this.session.currentStaffMemberId
            : undefined,
        channels: this.chatConversationsChannels.map((channel) => channel.name),
      },
      fetchPolicy: 'network-only',
    });
  }

  @restartableTask
  *getWhatsappConversationTask() {
    const whatsappConversations = yield this.apollo.query({
      query: getChatConversationsQuery,
      variables: {
        clientId: this.selectedClient.id,
        channels: ['WHATSAPP'],
      },
      fetchPolicy: 'network-only',
    });
    this.selectedConversation =
      whatsappConversations.chatConversations.edges[0].node;
  }

  @restartableTask
  *getWhatsappTemplatesTask() {
    return yield this.apollo.query(
      {
        query: getWhatsappTemplatesQuery,
        variables: {
          branchId: this.session.branchId,
        },
      },
      'whatsappTemplates'
    );
  }

  @restartableTask
  *refetchResourcesTask({ resource }) {
    let clientId;
    if (resource?.clientId) {
      clientId = resource.clientId;
    }

    yield this.getUnreadMessagesCountTask.perform();
    if (this.screen === 'chats-list') {
      yield this.getChatConversationsTask.perform();
    }
    if (this.screen === 'client-chat' && clientId === this.selectedClient.id) {
      yield this.observable.refetch();
      if (this.selectedConversation.channel === 'WHATSAPP') {
        yield this.getWhatsappConversationTask.perform();
      }
    }
  }

  @restartableTask
  *setScreenTask(screen) {
    const previousScreen = this.screen;
    if (screen === 'templates-list') {
      this.getWhatsappTemplatesTask.perform();
    }
    this.screen = screen;
    if (previousScreen === 'client-chat') {
      yield this.markConversationAsReadTask.perform();
      yield this.getUnreadMessagesCountTask.perform();
      this.lastMessage = null;
    }
    if (this.screen === 'chats-list') {
      yield this.getChatConversationsTask.perform();
    }
  }

  @enqueueTask
  *closeWindowChatTask() {
    yield this.setScreenTask.perform('');
    this.reset();
  }

  @restartableTask
  *getChatMessagesTask(variables = {}) {
    const queryResult = yield this.apollo.watchQuery(
      {
        query: getChatMessagesQuery,
        variables,
        fetchPolicy: 'network-only',
      },
      'chatMessages'
    );

    // sorting works fine on api but in the cache the messages are not sorted
    if (variables.after === 'MA==') {
      const sortedChatMessages = [...queryResult.edges].sort(
        (a, b) =>
          new Date(b.node.message.sentDateTime) -
          new Date(a.node.message.sentDateTime)
      );
      this.lastMessage = sortedChatMessages[0]?.node;
    }

    this.observable = getObservable(queryResult);
    return queryResult;
  }

  @task
  *sendMessageTask({
    text,
    fromMobile,
    templateId = null,
    operationName = 'sendMessage',
  }) {
    const { id: clientId, rawMobile: toMobile } = this.selectedClient;
    const { channel } = this.selectedConversation;

    const variables = {
      accountId: this.session.accountId,
      clientId,
      fromMobile: fromMobile.toString(),
      isMessageSeen: true,
      messageUuid: uuid(),
      staffFirstName: this.session.currentStaffMember?.firstName,
      staffLastName: this.session.currentStaffMember?.lastName,
      staffMemberId: this.session.currentStaffMemberId,
      toMobile,
      channel,
    };

    if (templateId) {
      variables.templateId = templateId;
    }
    if (text) {
      variables.text = text;
    }

    yield this.apollo.mutate(
      {
        mutation: sendMessageMutation,
        variables,
      },
      operationName
    );
  }

  @enqueueTask
  *sendUserInputTask(message) {
    const SMSFromMobile = this.session.branch.twoWayMessagingPhoneNumber
      ? this.session.branch.twoWayMessagingPhoneNumber
      : this.session.branch.marketing.branchSmsNumber;

    const fromMobile =
      this.selectedConversation.channel === 'SMS'
        ? SMSFromMobile
        : this.whatsapp.successfulConnection.displayPhoneNumber;

    yield this.sendMessageTask.perform({
      text: message,
      fromMobile,
    });

    // Track outbound message count
    this.pendo.trackEvent('Two-Way Chat - Outbound Message');

    // This is a workaround to ensure the chat messages are updated after the message is sent beacuse we don't receive an event from the backend
    setTimeout(() => {
      this.observable.refetch();
    }, 2000);
  }

  @dropTask
  *sendTemplateTask() {
    yield this.sendMessageTask.perform({
      fromMobile: this.whatsapp.successfulConnection.displayPhoneNumber,
      templateId: this.selectedTemplate.id,
      channel: 'WHATSAPP',
      operationName: 'sendTemplate',
      text: this.selectedTemplate.structure.body.text,
    });
  }
  @restartableTask
  *getUnreadMessagesCountTask() {
    return yield this.apollo.query({
      query: getUnreadMessagesCountQuery,
      variables: {
        accountId: this.session.accountId,
      },
      fetchPolicy: 'no-cache',
    });
  }

  @enqueueTask
  *markConversationAsReadTask() {
    const { id: clientId } = this.selectedClient;
    yield this.apollo.mutate(
      {
        mutation: updateChatMessagesSeenStatusMutation,
        variables: {
          clientId,
          messageUuids: [],
          seenStatus: true,
        },
      },
      'updateChatMessagesSeenStatus'
    );
  }

  @enqueueTask
  *markConversationAsUnreadTask(clientId, messageUuid) {
    yield this.apollo.mutate(
      {
        mutation: updateChatMessagesSeenStatusMutation,
        variables: {
          clientId,
          messageUuids: [messageUuid],
          seenStatus: false,
        },
      },
      'updateChatMessagesSeenStatus'
    );
    yield this.getChatConversationsTask.perform();
    yield this.getUnreadMessagesCountTask.perform();
  }

  @restartableTask
  *getBranchAiSettingsTask() {
    const result = yield this.apollo.query(
      {
        query: getBranchChatAiSettingsQuery,
        variables: {
          branchId: this.session.branchId,
        },
        fetchPolicy: 'network-only',
      },
      'branchChatAiSettings'
    );
    this.branchAiSettings = result;
    return result;
  }

  @enqueueTask
  *updateBranchAiSettingsTask(aiEnablementMode, testClientIds = null) {
    try {
      const input = { aiEnablementMode };

      if (testClientIds !== null) {
        input.testClientIds = testClientIds;
      }

      const result = yield this.apollo.mutate(
        {
          mutation: updateBranchChatAiSettingsMutation,
          variables: {
            branchId: this.session.branchId,
            input,
          },
        },
        'updateBranchChatAiSettings'
      );
      this.branchAiSettings = result;

      let message;
      let description;

      if (aiEnablementMode === 'ON') {
        message = this.intl.t('two-way-chat.ai-enabled-toast');
        description = this.intl.t('two-way-chat.ai-enabled-description');
      } else if (aiEnablementMode === 'TEST') {
        message = this.intl.t('two-way-chat.test-mode-toast');
        description = this.intl.t('two-way-chat.test-mode-toast-description');
      } else {
        message = this.intl.t('two-way-chat.ai-disabled-toast');
        description = this.intl.t('two-way-chat.ai-disabled-description');
      }

      this.notifications.success(message, description);
    } catch (error) {
      this.notifications.failure(this.intl.t('two-way-chat.ai-update-failed'));
    }
  }
}
